import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { prop } from 'ramda'
import { RootState } from 'app/store'
import { sessionActions, SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import DataKeys from 'k8s/DataKeys'

/**
 * Search/Filter/Sort parameters stored in session
 */
export interface SearchFilterSortParams {
  searchField?: string
  searchValue?: string
  filters?: Record<string, string>
  sortKey?: string
  sortDir?: 'asc' | 'desc'
}

/**
 * Pagination metadata structure stored in session
 */
export interface PaginationMetadata {
  currentMarker?: string | null
  nextMarker?: string | null
  hasMore?: boolean
  pageSize?: number
  totalCount?: number
  // Search/Filter/Sort params
  searchParams?: SearchFilterSortParams
}

/**
 * Configuration for the useServerSidePagination hook
 */
export interface UseServerSidePaginationConfig<T> {
  /** DataKey for the entity being paginated */
  dataKey: DataKeys
  /** Initial page size (default: 10) */
  initialPageSize?: number
  /** Selector to get data from Redux store */
  dataSelector: (state: RootState) => T[]
  /** Action to fetch initial/paginated data */
  fetchAction: (params: FetchActionParams) => Promise<FetchActionResult<T>>
  /** Action to fetch total count (accepts search/filter params for filtered counts) */
  countAction?: (params?: CountActionParams) => Promise<number>
  /** Action for polling updates - receives same params as fetchAction */
  pollAction?: (params: FetchActionParams) => Promise<T[]>
  /** Unique identifier field for the entity */
  uniqueIdentifier?: keyof T
  /** Optional entity-specific identifier for session storage */
  entityKey?: string
  /** Dependencies that trigger a reset (e.g., network_id for ports) */
  dependencies?: any[]
  /** Default sort key for initial load */
  defaultSortKey?: string
  /** Default sort direction for initial load (default: 'desc') */
  defaultSortDir?: 'asc' | 'desc'
}

/**
 * Parameters passed to the fetch action
 */
export interface FetchActionParams {
  limit: number
  marker?: string | null
  sortKey?: string
  sortDir?: 'asc' | 'desc'
  searchField?: string
  searchValue?: string
  filters?: Record<string, string>
}

/**
 * Parameters passed to the count action
 */
export interface CountActionParams {
  searchField?: string
  searchValue?: string
  filters?: Record<string, string>
}

/**
 * Result from the fetch action
 */
export interface FetchActionResult<T> {
  data: T[]
  nextMarker?: string | null
}

/**
 * Return type of the useServerSidePagination hook
 */
export interface UseServerSidePaginationResult<T> {
  // Data
  data: T[]
  paginatedData: T[]
  totalCount: number | undefined

  // Pagination state
  currentPage: number
  pageSize: number
  hasNextPage: boolean
  hasPrevPage: boolean

  // Loading states
  loading: boolean
  initialLoading: boolean
  polling: boolean

  // Actions
  handlePageChange: (newPage: number, marker?: string) => Promise<void>
  handlePageSizeChange: (newPageSize: number) => Promise<void>
  handleRefresh: () => Promise<void>
  handlePoll: () => Promise<void>
  resetPagination: () => void
  handleSearch: (params: SearchFilterSortParams) => Promise<void>
  handleClearSearch: () => Promise<void>

  // Search/Filter/Sort state
  searchParams: SearchFilterSortParams

  // Pagination metadata for the component
  paginationProps: {
    currentPage: number
    pageSize: number
    totalCount: number | undefined
    hasNextPage: boolean
    onPageChange: (newPage: number, marker?: string) => void
    onPageSizeChange: (newPageSize: number) => void
    nextMarker: string | null | undefined
  }
}

/**
 * Generic hook for server-side pagination.
 *
 * This hook provides a reusable pagination pattern that:
 * - Fetches data page by page using limit/marker
 * - Accumulates pages in cache for instant back-navigation
 * - Stores pagination metadata in session using generic pattern
 * - Handles page size changes and refresh
 *
 * @example
 * ```tsx
 * const {
 *   paginatedData,
 *   loading,
 *   paginationProps,
 * } = useServerSidePagination({
 *   dataKey: DataKeys.OpenstackPorts,
 *   dataSelector: portsSelector,
 *   fetchAction: async (params) => {
 *     const data = await fetchPorts(params)
 *     return { data, nextMarker: data[data.length - 1]?.id }
 *   },
 *   countAction: () => fetchPortsCount(),
 *   dependencies: [networkId],
 * })
 * ```
 */
export function useServerSidePagination<T extends Record<string, any>>(
  config: UseServerSidePaginationConfig<T>,
): UseServerSidePaginationResult<T> {
  const {
    dataKey,
    initialPageSize = 10,
    dataSelector,
    fetchAction,
    countAction,
    pollAction,
    uniqueIdentifier = 'id' as keyof T,
    entityKey,
    dependencies = [],
    defaultSortKey,
    defaultSortDir = 'desc',
  } = config

  const dispatch = useDispatch()
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))

  // Session key for this entity's pagination
  const paginationSessionKey = entityKey || dataKey

  // Get pagination metadata from session using generic pattern
  const paginationMeta = useMemo(() => {
    const pagination = session?.pagination as Record<string, PaginationMetadata> | undefined
    return pagination?.[paginationSessionKey]
  }, [session?.pagination, paginationSessionKey])

  // Get persisted page size from session
  const persistedPageSize = useMemo(() => {
    const pageSizes = session?.paginationPageSizes as Record<string, number> | undefined
    return pageSizes?.[paginationSessionKey]
  }, [session?.paginationPageSizes, paginationSessionKey])

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0)
  const [pageMarkers, setPageMarkers] = useState<(string | null)[]>([null])
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [polling, setPolling] = useState(false)
  const [pageSize, setPageSize] = useState(persistedPageSize || initialPageSize)
  const [totalCount, setTotalCount] = useState<number | undefined>(paginationMeta?.totalCount)

  // Build default search params with sort defaults
  const defaultSearchParams = useMemo<SearchFilterSortParams>(
    () => ({
      sortKey: defaultSortKey,
      sortDir: defaultSortKey ? defaultSortDir : undefined,
    }),
    [defaultSortKey, defaultSortDir],
  )

  // Search/Filter/Sort state - initialize from session or use defaults
  const [searchParams, setSearchParams] = useState<SearchFilterSortParams>(
    paginationMeta?.searchParams || defaultSearchParams,
  )

  // Refs
  const isInitialMount = useRef(true)
  const hasInitialDataLoaded = useRef(false)

  // Get data from Redux
  const data = useSelector(dataSelector)

  // Update session pagination metadata
  const updatePaginationMeta = useCallback(
    (meta: Partial<PaginationMetadata>) => {
      const currentPagination = (session?.pagination as Record<string, PaginationMetadata>) || {}
      dispatch(
        sessionActions.updateSession({
          pagination: {
            ...currentPagination,
            [paginationSessionKey]: {
              ...currentPagination[paginationSessionKey],
              ...meta,
            },
          },
        } as any),
      )
    },
    [dispatch, session?.pagination, paginationSessionKey],
  )

  // Update session page size
  const updatePageSizeInSession = useCallback(
    (newPageSize: number) => {
      const currentPageSizes = (session?.paginationPageSizes as Record<string, number>) || {}
      dispatch(
        sessionActions.updateSession({
          paginationPageSizes: {
            ...currentPageSizes,
            [paginationSessionKey]: newPageSize,
          },
        } as any),
      )
    },
    [dispatch, session?.paginationPageSizes, paginationSessionKey],
  )

  // Calculate paginated data for current page
  const paginatedData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return []
    }
    const startIndex = currentPage * pageSize
    const endIndex = startIndex + pageSize
    return data.slice(startIndex, endIndex)
  }, [data, currentPage, pageSize])

  // Calculate if there's a next page
  const hasNextPage = useMemo(() => {
    // Check if we have a marker for the next page
    const nextPageMarker = pageMarkers[currentPage + 1]
    if (nextPageMarker) {
      return true
    }

    // Check based on total count
    if (totalCount != null) {
      const estimatedMaxPage = Math.ceil(totalCount / pageSize) - 1
      return currentPage < estimatedMaxPage
    }

    // Check session metadata
    return paginationMeta?.hasMore === true && paginationMeta?.nextMarker != null
  }, [currentPage, pageMarkers, totalCount, pageSize, paginationMeta])

  const hasPrevPage = useMemo(() => currentPage > 0, [currentPage])

  // Fetch data for a specific page
  const fetchPage = useCallback(
    async (
      marker: string | null,
      isFirstPage: boolean,
      overrideSearchParams?: SearchFilterSortParams,
    ) => {
      setLoading(true)
      try {
        const effectiveSearchParams = overrideSearchParams ?? searchParams
        const result = await fetchAction({
          limit: pageSize,
          marker: marker || undefined,
          sortKey: effectiveSearchParams.sortKey,
          sortDir: effectiveSearchParams.sortDir,
          searchField: effectiveSearchParams.searchField,
          searchValue: effectiveSearchParams.searchValue,
          filters: effectiveSearchParams.filters,
        })

        // Update pagination metadata
        const nextMarker =
          result.data?.length === pageSize && result.data?.length > 0
            ? result.nextMarker || (result.data[result.data.length - 1][uniqueIdentifier] as string)
            : null

        const dataLength = data?.length || 0
        const estimatedLoadedAfterThis = marker
          ? dataLength + result.data.length
          : result.data.length
        const hasMore =
          totalCount != null
            ? estimatedLoadedAfterThis < totalCount
            : result.data?.length === pageSize

        updatePaginationMeta({
          currentMarker: marker,
          nextMarker,
          hasMore,
          pageSize,
          totalCount,
          searchParams: overrideSearchParams ?? searchParams,
        })

        // Store next marker for navigation
        if (nextMarker) {
          setPageMarkers((prev) => {
            const updated = [...prev]
            updated[currentPage + 1] = nextMarker
            return updated
          })
        }

        return result.data
      } finally {
        setLoading(false)
        setInitialLoading(false)
      }
    },
    [
      fetchAction,
      pageSize,
      uniqueIdentifier,
      data?.length,
      totalCount,
      updatePaginationMeta,
      currentPage,
      searchParams,
    ],
  )

  // Handle page change
  const handlePageChange = useCallback(
    async (newPage: number, marker?: string) => {
      const isGoingBackward = newPage < currentPage

      // Check if cache has enough data for the requested page
      const cacheLength = data?.length || 0
      const startIndex = newPage * pageSize
      const endIndex = startIndex + pageSize
      const cacheHasEnoughData = cacheLength >= endIndex
      const cacheHasAllData = totalCount != null && cacheLength >= totalCount

      // Update page immediately
      setCurrentPage(newPage)

      // Skip fetch if we already have the data in cache
      if (isGoingBackward || cacheHasEnoughData || cacheHasAllData) {
        return
      }

      // Fetch new data using the marker
      const requestMarker = marker || pageMarkers[newPage] || null
      if (marker && !pageMarkers[newPage]) {
        setPageMarkers((prev) => {
          const updated = [...prev]
          updated[newPage] = marker
          return updated
        })
      }

      await fetchPage(requestMarker, newPage === 0)
    },
    [currentPage, pageMarkers, data?.length, pageSize, totalCount, fetchPage],
  )

  // Handle page size change
  const handlePageSizeChange = useCallback(
    async (newPageSize: number) => {
      setPageSize(newPageSize)
      setCurrentPage(0)
      setPageMarkers([null])
      updatePageSizeInSession(newPageSize)

      // Check if cache has enough data
      const cacheLength = data?.length || 0
      const cacheHasEnoughData = cacheLength >= newPageSize
      const cacheHasAllData = totalCount != null && cacheLength >= totalCount

      if (cacheHasEnoughData || cacheHasAllData) {
        return
      }

      // Fetch with new page size
      setLoading(true)
      try {
        await fetchAction({
          limit: newPageSize,
          marker: undefined,
          sortKey: searchParams.sortKey,
          sortDir: searchParams.sortDir,
          searchField: searchParams.searchField,
          searchValue: searchParams.searchValue,
          filters: searchParams.filters,
        })
      } finally {
        setLoading(false)
      }
    },
    [
      data?.length,
      totalCount,
      updatePageSizeInSession,
      fetchAction,
      searchParams.sortKey,
      searchParams.sortDir,
      searchParams.searchField,
      searchParams.searchValue,
      searchParams.filters,
    ],
  )

  // Handle refresh - preserves current search/filter/sort params
  const handleRefresh = useCallback(async () => {
    setCurrentPage(0)
    setPageMarkers([null])
    setLoading(true)

    try {
      // Fetch count with current search/filter params
      if (countAction) {
        const count = await countAction({
          searchField: searchParams.searchField,
          searchValue: searchParams.searchValue,
          filters: searchParams.filters,
        })
        setTotalCount(count)
        updatePaginationMeta({ totalCount: count })
      }

      // Fetch first page with current search/filter/sort params
      await fetchAction({
        limit: pageSize,
        marker: undefined,
        sortKey: searchParams.sortKey,
        sortDir: searchParams.sortDir,
        searchField: searchParams.searchField,
        searchValue: searchParams.searchValue,
        filters: searchParams.filters,
      })
    } finally {
      setLoading(false)
    }
  }, [countAction, fetchAction, pageSize, updatePaginationMeta, searchParams])

  // Reset pagination
  const resetPagination = useCallback(() => {
    setCurrentPage(0)
    setPageMarkers([null])
    setTotalCount(undefined)
  }, [])

  // Handle polling - calls pollAction with current pagination params
  const handlePoll = useCallback(async () => {
    if (!pollAction) {
      return
    }

    setPolling(true)
    try {
      await pollAction({
        limit: pageSize,
        marker: pageMarkers[currentPage] || undefined,
        sortKey: searchParams.sortKey,
        sortDir: searchParams.sortDir,
        searchField: searchParams.searchField,
        searchValue: searchParams.searchValue,
        filters: searchParams.filters,
      })
    } finally {
      setPolling(false)
    }
  }, [
    pollAction,
    pageSize,
    pageMarkers,
    currentPage,
    searchParams.sortKey,
    searchParams.sortDir,
    searchParams.searchField,
    searchParams.searchValue,
    searchParams.filters,
  ])

  // Handle search/filter/sort change - resets to page 1 and fetches with new params
  const handleSearch = useCallback(
    async (newSearchParams: SearchFilterSortParams) => {
      // Update local state
      setSearchParams(newSearchParams)
      // Reset pagination
      setCurrentPage(0)
      setPageMarkers([null])
      setLoading(true)

      try {
        // Fetch count with search/filter params to get filtered count
        if (countAction) {
          const count = await countAction({
            searchField: newSearchParams.searchField,
            searchValue: newSearchParams.searchValue,
            filters: newSearchParams.filters,
          })
          setTotalCount(count)
        }

        // Fetch first page with new search params
        await fetchPage(null, true, newSearchParams)
      } finally {
        setLoading(false)
      }
    },
    [countAction, fetchPage],
  )

  // Clear search/filter/sort and reload (preserves default sort)
  const handleClearSearch = useCallback(async () => {
    // Reset to default params (preserves default sort key/dir)
    setSearchParams(defaultSearchParams)
    setCurrentPage(0)
    setPageMarkers([null])
    setLoading(true)

    try {
      // Fetch count without filters (total count)
      if (countAction) {
        const count = await countAction({})
        setTotalCount(count)
      }

      // Fetch first page with default sort params
      await fetchPage(null, true, defaultSearchParams)
    } finally {
      setLoading(false)
    }
  }, [countAction, fetchPage, defaultSearchParams])

  // Initial load
  useEffect(() => {
    if (hasInitialDataLoaded.current) {
      return
    }
    hasInitialDataLoaded.current = true

    const loadInitialData = async () => {
      setInitialLoading(true)
      setLoading(true)

      try {
        // Fetch count first if available
        if (countAction) {
          const count = await countAction({
            searchField: searchParams.searchField,
            searchValue: searchParams.searchValue,
            filters: searchParams.filters,
          })
          setTotalCount(count)
          updatePaginationMeta({ totalCount: count })
        }

        // Fetch first page with default sort params
        await fetchPage(null, true)
      } finally {
        setLoading(false)
        setInitialLoading(false)
      }
    }

    loadInitialData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reset when dependencies change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Reset pagination and reload
    resetPagination()
    hasInitialDataLoaded.current = false

    const reloadData = async () => {
      setLoading(true)
      try {
        if (countAction) {
          const count = await countAction({
            searchField: searchParams.searchField,
            searchValue: searchParams.searchValue,
            filters: searchParams.filters,
          })
          setTotalCount(count)
          updatePaginationMeta({ totalCount: count })
        }
        await fetchPage(null, true)
      } finally {
        setLoading(false)
      }
    }

    reloadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, searchParams])

  // Build pagination props for the component
  const paginationProps = useMemo(
    () => ({
      currentPage,
      pageSize,
      totalCount,
      hasNextPage,
      onPageChange: handlePageChange,
      onPageSizeChange: handlePageSizeChange,
      nextMarker: paginationMeta?.nextMarker,
    }),
    [
      currentPage,
      pageSize,
      totalCount,
      hasNextPage,
      handlePageChange,
      handlePageSizeChange,
      paginationMeta?.nextMarker,
    ],
  )

  return {
    // Data
    data,
    paginatedData,
    totalCount,

    // Pagination state
    currentPage,
    pageSize,
    hasNextPage,
    hasPrevPage,

    // Loading states
    loading,
    initialLoading,
    polling,

    // Actions
    handlePageChange,
    handlePageSizeChange,
    handleRefresh,
    handlePoll,
    resetPagination,
    handleSearch,
    handleClearSearch,

    // Search/Filter/Sort state
    searchParams,

    // Props for pagination component
    paginationProps,
  }
}

export default useServerSidePagination
